import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  adminListThreads, adminListGroups, adminCreateGroup, adminUpdateGroup,
  moderateThread, moderateGroup, adminListUsers, adminUpdateUserRole,
} from "@/lib/api";
import { MessageSquare, Users, Shield, EyeOff, Eye, Trash2, Plus, Loader2, Save, X } from "lucide-react";

const TABS = [["discussions", "Discussions"], ["qna", "Q&A"], ["groups", "Groups"], ["moderators", "Moderators"]];

export default function AdminCommunity() {
  const [tab, setTab] = useState("discussions");
  const [threads, setThreads] = useState([]);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [gForm, setGForm] = useState({ name: "", category: "namasmarana", mantra_deva: "", mantra_en: "", deity: "", region: "", description: "", image_url: "", target: "" });
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      if (tab === "groups") setGroups(await adminListGroups());
      else if (tab === "moderators") setUsers(await adminListUsers());
      else setThreads(await adminListThreads(tab === "qna" ? "qna" : "discussion"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const modThread = async (id, action) => {
    if (action === "delete" && !window.confirm("Delete this thread and its replies?")) return;
    await moderateThread(id, action);
    setThreads((t) => action === "delete" ? t.filter((x) => x.id !== id) : t.map((x) => x.id === id ? { ...x, status: action === "hide" ? "hidden" : "open" } : x));
  };
  const modGroup = async (id, action) => {
    if (action === "delete" && !window.confirm("Delete this group?")) return;
    await moderateGroup(id, action);
    setGroups((g) => action === "delete" ? g.filter((x) => x.id !== id) : g.map((x) => x.id === id ? { ...x, status: action === "hide" ? "hidden" : "open" } : x));
  };
  const createG = async (e) => {
    e.preventDefault();
    if (!gForm.name.trim()) return;
    if (gForm.category === "namasmarana" && !gForm.mantra_deva.trim()) { toast("A namasmarana group needs a mantra"); return; }
    setBusy("create");
    try {
      const g = await adminCreateGroup({ ...gForm, target: parseInt(gForm.target || "0", 10) });
      setGroups((gs) => [g, ...gs]); setCreating(false);
      setGForm({ name: "", category: "namasmarana", mantra_deva: "", mantra_en: "", deity: "", region: "", description: "", image_url: "", target: "" });
      toast("Group created");
    } catch (err) { toast("Could not create", { description: err?.response?.data?.detail || "Try again." }); }
    finally { setBusy(""); }
  };
  const setRole = async (u, role) => {
    await adminUpdateUserRole(u.id, role);
    setUsers((us) => us.map((x) => x.id === u.id ? { ...x, role } : x));
    toast(`${u.name || u.email} → ${role}`);
  };

  return (
    <div data-testid="admin-community">
      <h1 className="font-serif-latin text-3xl text-ink">Community</h1>
      <p className="mt-1 text-sm text-ink-3">Manage discussions, Q&A and Namasmarana groups, and appoint moderators.</p>

      <div className="mt-6 flex flex-wrap gap-2 border-b border-line">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`community-tab-${k}`}
            className={`-mb-px border-b-2 px-4 py-2.5 text-sm transition-colors ${tab === k ? "border-saffron text-saffron" : "border-transparent text-ink-2 hover:text-ink"}`}>{l}</button>
        ))}
      </div>

      {loading ? <div className="flex items-center gap-2 py-10 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div> : (
        <div className="mt-6">
          {/* Threads */}
          {(tab === "discussions" || tab === "qna") && (
            <div className="divide-y divide-line border-y border-line">
              {threads.length === 0 ? <p className="py-10 text-center text-ink-3">Nothing here yet.</p> : threads.map((t) => (
                <div key={t.id} data-testid={`admin-thread-${t.id}`} className={`flex items-center gap-4 py-4 ${t.status === "hidden" ? "opacity-50" : ""}`}>
                  <MessageSquare className="h-4 w-4 shrink-0 text-ink-3" />
                  <div className="min-w-0 flex-1">
                    <Link to={`/${tab === "qna" ? "qna" : "discussions"}/${t.id}`} className="font-serif-latin text-ink hover:text-saffron">{t.title}</Link>
                    <p className="text-xs text-ink-3">{t.author_name} · {t.reply_count} replies · {t.upvotes} upvotes{t.status === "hidden" ? " · HIDDEN" : ""}</p>
                  </div>
                  <button onClick={() => modThread(t.id, t.status === "hidden" ? "open" : "hide")} className="p-2 text-ink-2 hover:text-saffron" title={t.status === "hidden" ? "Unhide" : "Hide"}>{t.status === "hidden" ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
                  <button onClick={() => modThread(t.id, "delete")} className="p-2 text-red-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}

          {/* Groups */}
          {tab === "groups" && (
            <>
              <div className="mb-4 flex justify-end">
                <button onClick={() => setCreating((v) => !v)} data-testid="admin-group-new" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white"><Plus className="h-4 w-4" /> New group</button>
              </div>
              {creating && (
                <form onSubmit={createG} className="mb-5 border border-line bg-surface p-5">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <input value={gForm.name} onChange={(e) => setGForm({ ...gForm, name: e.target.value })} data-testid="admin-group-name" placeholder="Group name" className="border border-line bg-parchment px-3 py-2 text-ink focus:border-saffron focus:outline-none" />
                    <select value={gForm.category} onChange={(e) => setGForm({ ...gForm, category: e.target.value })} data-testid="admin-group-category" className="border border-line bg-parchment px-3 py-2 text-ink focus:border-saffron focus:outline-none">
                      <option value="namasmarana">Namasmarana</option>
                      <option value="deity">Deity</option>
                      <option value="topic">Topic</option>
                      <option value="region">Region</option>
                      <option value="purpose">Purpose</option>
                      <option value="general">General</option>
                    </select>
                  </div>
                  {gForm.category === "namasmarana" && (
                    <div className="mt-3 flex gap-3">
                      <input value={gForm.mantra_deva} onChange={(e) => setGForm({ ...gForm, mantra_deva: e.target.value })} data-testid="admin-group-mantra" placeholder="Mantra (Devanagari)" className="flex-1 border border-line bg-parchment px-3 py-2 font-deva text-ink focus:border-saffron focus:outline-none" />
                      <input value={gForm.target} onChange={(e) => setGForm({ ...gForm, target: e.target.value })} type="number" placeholder="Japa target" className="w-40 border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
                    </div>
                  )}
                  {gForm.category === "deity" && <input value={gForm.deity} onChange={(e) => setGForm({ ...gForm, deity: e.target.value })} placeholder="Deity (e.g. Shiva)" className="mt-3 w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />}
                  {gForm.category === "region" && <input value={gForm.region} onChange={(e) => setGForm({ ...gForm, region: e.target.value })} placeholder="Region (e.g. Kashi)" className="mt-3 w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />}
                  <input value={gForm.image_url} onChange={(e) => setGForm({ ...gForm, image_url: e.target.value })} placeholder="Image URL (optional)" className="mt-3 w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
                  <textarea value={gForm.description} onChange={(e) => setGForm({ ...gForm, description: e.target.value })} rows={2} placeholder="Description" className="mt-3 w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
                  <div className="mt-3 flex gap-2">
                    <button type="submit" disabled={busy === "create"} data-testid="admin-group-save" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">{busy === "create" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Create</button>
                    <button type="button" onClick={() => setCreating(false)} className="border border-line px-4 py-2 text-sm text-ink-2"><X className="h-4 w-4" /></button>
                  </div>
                </form>
              )}
              <div className="divide-y divide-line border-y border-line">
                {groups.length === 0 ? <p className="py-10 text-center text-ink-3">No groups yet.</p> : groups.map((g) => (
                  <div key={g.id} data-testid={`admin-group-${g.id}`} className={`flex items-center gap-4 py-4 ${g.status === "hidden" ? "opacity-50" : ""}`}>
                    <Users className="h-4 w-4 shrink-0 text-ink-3" />
                    <div className="min-w-0 flex-1">
                      <p className="font-serif-latin text-ink">{g.name} {g.mantra_deva && <span className="font-deva text-saffron">{g.mantra_deva}</span>}</p>
                      <p className="text-xs text-ink-3"><span className="uppercase tracking-wider text-gold">{g.category || "namasmarana"}</span> · {g.member_count} members{g.category === "namasmarana" ? ` · ${g.total} japa` : ""}{g.status === "hidden" ? " · HIDDEN" : ""}</p>
                    </div>
                    <button onClick={() => modGroup(g.id, g.status === "hidden" ? "open" : "hide")} className="p-2 text-ink-2 hover:text-saffron">{g.status === "hidden" ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
                    <button onClick={() => modGroup(g.id, "delete")} className="p-2 text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Moderators */}
          {tab === "moderators" && (
            <div className="divide-y divide-line border-y border-line">
              {users.map((u) => (
                <div key={u.id} data-testid={`mod-user-${u.id}`} className="flex items-center gap-4 py-4">
                  <Shield className={`h-4 w-4 shrink-0 ${u.role === "moderator" || u.role === "admin" ? "text-saffron" : "text-ink-3"}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-ink">{u.name || "—"} <span className="text-xs text-ink-3">{u.email}</span></p>
                    <p className="text-xs text-ink-3">Role: {u.role}</p>
                  </div>
                  {u.role !== "admin" && (
                    u.role === "moderator"
                      ? <button onClick={() => setRole(u, "user")} data-testid={`mod-remove-${u.id}`} className="border border-line px-3 py-1.5 text-xs text-ink-2 hover:text-saffron">Remove moderator</button>
                      : <button onClick={() => setRole(u, "moderator")} data-testid={`mod-add-${u.id}`} className="bg-[var(--saffron)] px-3 py-1.5 text-xs font-medium text-white">Make moderator</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
